import { toPhoneNumber } from '@hourly/utilities';
import { type Column, Table } from '@hourly-hds/components';
import { Edit } from '@hourly-hds/icons/glyph';
import { useState } from 'react';
import EditUserDialog from './components/EditUserDialog/EditUserDialog';
import UserStatusChip from './components/UserStatusChip';
import { useUsers } from './data/hooks/useUsers';
import { User, UserStatus } from './data/models/users.model';
import styles from './users.module.css';
import { TabContentSpinner } from '../../../_shared/components/TabContentSpinner';

export default function UsersIndex({ partnerId }: { partnerId: string }) {
  const { users, isFetchUsersSettled, editUser, editUserErrorMessage, clearEditUserError } =
    useUsers(partnerId);

  const [selectedUser, setSelectedUser] = useState<User | undefined>();

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
  };

  const handleOnClose = () => {
    setSelectedUser(undefined);
    clearEditUserError();
  };

  const handleOnFinish = async (userToEdit: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    mobileNumber: string;
  }) => {
    await editUser(userToEdit, {
      onSuccess: () => {
        setSelectedUser(undefined);
      },
    });
  };

  const columns: Column<User>[] = [
    {
      header: 'User',
      accessorKey: 'fullName',
    },
    {
      header: 'Email',
      accessorKey: 'email',
    },
    {
      header: 'Mobile Number',
      accessorKey: 'mobileNumber',
      cell: (info: { getValue: () => string }) => toPhoneNumber(info.getValue()),
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: (info: { getValue: () => UserStatus }) => <UserStatusChip status={info.getValue()} />,
    },
    {
      accessorKey: 'edit',
      align: 'center',
      cell: ({ row }: { row: { original: User } }) => (
        <Edit
          className={styles.editIcon}
          aria-label="edit"
          onClick={() => handleEditUser(row.original)}
        />
      ),
      header: () => <Table.EmptyHeader>Edit Column</Table.EmptyHeader>,
    },
  ] as Column<User>[];

  if (!isFetchUsersSettled) {
    return <TabContentSpinner />;
  }
  return (
    <div className={styles.content}>
      <Table data={users} columns={columns} />
      {selectedUser && (
        <EditUserDialog
          currentUser={selectedUser!}
          onClose={handleOnClose}
          onFinish={handleOnFinish}
          errorMessage={editUserErrorMessage}
        />
      )}
    </div>
  );
}
